import { BasicButton } from 'src/design/atoms/BasicButton';
import { Box } from 'src/design/atoms/Box';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconThumbDownFilled, IconThumbUpFilled } from '@tabler/icons-react';
import React, { useId } from 'react';
import { FeedbackReasonCountDto } from 'src/api/FiretrolApiClient';
import {
  FEEDBACK_RATING_DOWN,
  FEEDBACK_RATING_UP,
  feedbackReasonLabel,
} from 'src/app/chat/messageFeedback';
import { FeedbackRating } from 'src/routes/Settings/AIAssistant/aiAssistantSearchParams';
import { formatNumber } from 'src/utils';

export interface IFeedbackFacetChipsProps {
  upCount: number;
  downCount: number;
  reasonCounts: FeedbackReasonCountDto[];
  rating: FeedbackRating | undefined;
  reasons: string[];
  onRatingToggle: (rating: FeedbackRating) => void;
  onReasonToggle: (reason: string) => void;
}

const SELECTED_STYLE: React.CSSProperties = {
  borderColor: 'var(--mantine-color-blue-6)',
  boxShadow: 'inset 0 0 0 1px var(--mantine-color-blue-6)',
};

interface IFacetChipProps {
  label: string;
  count: number;
  selected: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}

const FacetChip: React.FC<IFacetChipProps> = ({
  label,
  count,
  selected,
  icon,
  onClick,
}) => (
  <BasicButton
    variant="default"
    size="compact-sm"
    radius="xl"
    fw={400}
    aria-pressed={selected}
    leftSection={icon}
    onClick={onClick}
    style={selected ? SELECTED_STYLE : undefined}
  >
    <Text span size="sm" c="bright">
      {label}
    </Text>
    <Text span size="sm" fw={700} ml={6} c={count === 0 ? 'dimmed' : 'bright'}>
      {formatNumber(count)}
    </Text>
  </BasicButton>
);

/** Counts cover the date, user and search filters only, so they stay put while rating and reason chips toggle. */
export const FeedbackFacetChips: React.FC<IFeedbackFacetChipsProps> = ({
  upCount,
  downCount,
  reasonCounts,
  rating,
  reasons,
  onRatingToggle,
  onReasonToggle,
}) => {
  const labelId = useId();

  return (
    <Stack gap={6}>
      <Text id={labelId} size="sm" fw={500} c="bright">
        Filter by rating and reason
      </Text>
      <Group gap="xs" wrap="wrap" role="group" aria-labelledby={labelId}>
        <FacetChip
          label="Helpful"
          count={upCount}
          selected={rating === FEEDBACK_RATING_UP}
          icon={
            <Box c="green.7" display="flex">
              <IconThumbUpFilled size={14} aria-hidden />
            </Box>
          }
          onClick={() => onRatingToggle(FEEDBACK_RATING_UP)}
        />
        <FacetChip
          label="Not helpful"
          count={downCount}
          selected={rating === FEEDBACK_RATING_DOWN}
          icon={
            <Box c="red.7" display="flex">
              <IconThumbDownFilled size={14} aria-hidden />
            </Box>
          }
          onClick={() => onRatingToggle(FEEDBACK_RATING_DOWN)}
        />
        <Divider orientation="vertical" mx={4} />
        {reasonCounts.map(({ reason, count }) => (
          <FacetChip
            key={reason}
            label={feedbackReasonLabel(reason)}
            count={count}
            selected={reasons.includes(reason)}
            onClick={() => onReasonToggle(reason)}
          />
        ))}
      </Group>
    </Stack>
  );
};
